import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { motion } from 'framer-motion';
import logoCursory from './logo-cursory.png';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://jfzevsikadlhneceeyhf.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmemV2c2lrYWRsaG5lY2VleWhmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIxMzYwNTksImV4cCI6MjA2NzcxMjA1OX0.F_-FepfmnEDkdhSZIv6n_Wr8ankoGSfGibvanTDK-L4';
const supabase = createClient(supabaseUrl, supabaseKey);

function useCourses({ type, selectedCategory, search, sort }) {
  const [courses, setCourses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function fetchCourses() {
      setLoading(true);
      
      // Test the connection first
      console.log('Testing Supabase connection...');
      console.log('URL:', supabaseUrl);
      console.log('Key length:', supabaseKey.length);
      
      try {
        let query = supabase.from('courses').select('*'); // changed to lowercase
        if (type) query = query.eq('type', type);
        if (selectedCategory) query = query.eq('category', selectedCategory);
        const { data, error } = await query;
        console.log('Supabase fetch result:', { data, error }); // Debug log
        
        if (error) {
          console.error('Supabase error details:', error);
        }
        
        let filtered = data || [];
        if (search) {
          const q = search.toLowerCase();
          filtered = filtered.filter(course =>
            (course.title || '').toLowerCase().includes(q) ||
            (course.platform || '').toLowerCase().includes(q) ||
            (course.category || '').toLowerCase().includes(q)
          );
        }
        // Sorting
        if (sort === 'rating-desc') {
          filtered = [...filtered].sort((a, b) => b.rating - a.rating);
        } else if (sort === 'rating-asc') {
          filtered = [...filtered].sort((a, b) => a.rating - b.rating);
        } else if (sort === 'duration-desc') {
          filtered = [...filtered].sort((a, b) => {
            const getHours = (str) => parseFloat((str.match(/\d+(\.\d+)?/) || [0])[0]);
            return getHours(b.duration) - getHours(a.duration);
          });
        } else if (sort === 'duration-asc') {
          filtered = [...filtered].sort((a, b) => {
            const getHours = (str) => parseFloat((str.match(/\d+(\.\d+)?/) || [0])[0]);
            return getHours(a.duration) - getHours(b.duration);
          });
        }
        setCourses(filtered);
      } catch (err) {
        console.error('Exception during Supabase fetch:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchCourses();
  }, [type, selectedCategory, search, sort]);

  return { courses, loading };
}

// Custom hook for scroll-based reveal animations
function useRevealOnScroll(options = {}) {
  const [isRevealed, setIsRevealed] = React.useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          // Unobserve after revealing to prevent re-triggering
          if (options.once !== false) {
            observer.unobserve(element);
          }
        } else if (options.once === false) {
          setIsRevealed(false);
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '0px 0px -50px 0px',
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [options.threshold, options.rootMargin, options.once]);

  return [elementRef, isRevealed];
}

// Reveal component wrapper
function RevealOnScroll({ children, className = '', animationType = 'reveal-on-scroll', stagger = '', ...props }) {
  const [ref, isRevealed] = useRevealOnScroll();
  
  const animationClass = `${animationType} ${stagger} ${isRevealed ? 'revealed' : ''}`;
  
  return (
    <div ref={ref} className={`${animationClass} ${className}`} {...props}>
      {children}
    </div>
  );
}

function useTheme() {
  const [dark, setDark] = React.useState(() => document.documentElement.classList.contains('dark'));
  React.useEffect(() => {
    if (dark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [dark]);
  return [dark, setDark];
}

function ThemeSwitcher() {
  const [dark, setDark] = useTheme();
  return (
    <button
        className="ml-auto p-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 glass-button btn-ripple text-[#1E3A8A] dark:text-white transition"
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => setDark(d => !d)}
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {dark ? (
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path d="M17.657 16.243A8 8 0 017.757 6.343 8.001 8.001 0 1017.657 16.243z" fill="currentColor"/></svg>
      ) : (
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/><path d="M12 1v2m0 18v2m11-11h-2M3 12H1m16.95 6.95l-1.414-1.414M6.464 6.464L5.05 5.05m12.02 0l-1.414 1.414M6.464 17.536l-1.414 1.414" stroke="currentColor" strokeWidth="2"/></svg>
      )}
    </button>
  );
}

function Navbar({ onNavClick }) {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const isDark = document.documentElement.classList.contains('dark');

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  React.useEffect(() => {
    setMenuOpen(false);
  }, [window.location.pathname]);

  return (
    <header className="sticky-nav z-30">
      <div className={`nav-content ${isScrolled ? 'scrolled' : ''}`}> 
        <div className="nav-container">
          <nav className="w-full flex items-center justify-between">
            <div className="nav-brand">
              <img src={logoCursory} alt="Cursory logo" className="nav-brand-logo" />
              <span className="nav-brand-name">Cursory</span>
            </div>
            {/* Hamburger for mobile */}
            <button
              className="md:hidden ml-auto p-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((open) => !open)}
              style={{marginLeft: 'auto'}}
            >
              {menuOpen ? (
                <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round"/></svg>
              ) : (
                <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round"/></svg>
              )}
            </button>
            {/* Nav links */}
            <div
              className={`hidden md:flex items-center gap-2 md:gap-6 ml-6`}
            >
              <Link to="/" className="nav-link">Home</Link>
              <button type="button" onClick={onNavClick?.suggest} className="nav-link">Suggest Course</button>
              <button type="button" onClick={onNavClick?.report} className="nav-link">Report Issue</button>
              <div className="ml-2 flex-shrink-0">
                <ThemeSwitcher />
              </div>
            </div>
            {/* Mobile dropdown */}
            {menuOpen && (
              <div className="absolute top-full left-0 w-full bg-white dark:bg-blue-950 shadow-lg rounded-b-2xl flex flex-col items-center py-4 md:hidden animate-fade-in z-40">
                <Link to="/" className="nav-link w-full text-center py-3" onClick={() => setMenuOpen(false)}>Home</Link>
                <button type="button" onClick={() => { setMenuOpen(false); onNavClick?.suggest(); }} className="nav-link w-full text-center py-3">Suggest Course</button>
                <button type="button" onClick={() => { setMenuOpen(false); onNavClick?.report(); }} className="nav-link w-full text-center py-3">Report Issue</button>
                <div className="mt-2 flex-shrink-0">
          <ThemeSwitcher />
                </div>
              </div>
            )}
          </nav>
        </div>
      </div>
      <div className={`nav-placeholder ${isScrolled ? 'active' : ''}`}></div>
    </header>
  );
}

function ScrollToCourseSection({ sectionRef }) {
  const location = useLocation();
  React.useEffect(() => {
    if (location.pathname === '/free' || location.pathname === '/paid') {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100); // wait for render
    }
  }, [location, sectionRef]);
  return null;
}

function Hero({ onNavigate, isLoading = false }) {
  return (
    <section className="relative hero-section flex flex-col items-center justify-center min-h-[60vh] mt-16 pt-8 w-full" aria-label="Hero">
      {/* Subtle blue radial background for hero focus */}
      <div className="absolute inset-0 z-0 pointer-events-none flex items-center justify-center">
        <div className="w-[700px] h-[400px] rounded-full bg-gradient-to-br from-blue-100 via-blue-200 to-transparent opacity-70 blur-3xl" style={{filter:'blur(60px)'}}></div>
      </div>
      <div className="relative z-10 flex flex-col items-center w-full px-4">
        <RevealOnScroll animationType="hero-reveal" stagger="reveal-stagger-1">
          {isLoading ? (
            <div className="skeleton-text skeleton-delay-1" style={{ width: '400px', height: '3.5rem', maxWidth: '90vw' }} />
          ) : (
            <h1 className="hero-heading gradient-text">
              Curated Course Hunt<br />Experience
            </h1>
          )}
        </RevealOnScroll>
        <RevealOnScroll animationType="hero-reveal" stagger="reveal-stagger-2">
          {isLoading ? (
            <div className="skeleton-text skeleton-delay-2" style={{ width: '500px', height: '2rem', maxWidth: '90vw' }} />
          ) : (
            <p className="hero-subtitle text-xl sm:text-2xl text-slate-700 dark:text-slate-200 text-center pt-2 max-w-2xl mx-auto mb-8 font-medium transition-colors duration-500">
        Explore the internet’s most useful free and paid courses. All handpicked, neatly categorized, and ranked with Cursory Ratings. Save hours of scrolling and start learning what truly matters.
      </p>
          )}
        </RevealOnScroll>
        <RevealOnScroll animationType="hero-reveal" stagger="reveal-stagger-3">
          {isLoading ? (
            <div className="skeleton-text skeleton-delay-3" style={{ width: '300px', height: '1.2rem', maxWidth: '90vw' }} />
          ) : (
            <p className="text-lg text-slate-600 dark:text-slate-300 text-center mb-6 font-semibold transition-colors duration-500">
              Choose your path to start exploring curated courses
            </p>
          )}
        </RevealOnScroll>
        <RevealOnScroll animationType="hero-reveal" stagger="reveal-stagger-4">
          {isLoading ? (
            <SkeletonHeroButtons />
          ) : (
            <div className="hero-buttons flex flex-col sm:flex-row gap-8 w-full justify-center">
        <button
                className="px-10 py-4 text-xl font-bold rounded-full bg-gradient-to-r from-green-500 to-green-400 text-white shadow-lg hover:scale-105 hover:shadow-xl active:scale-98 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          onClick={() => onNavigate('/free')}
              >FREE COURSES</button>
        <button
                className="px-10 py-4 text-xl font-bold rounded-full bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg hover:scale-105 hover:shadow-xl active:scale-98 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          onClick={() => onNavigate('/paid')}
              >PAID COURSES</button>
            </div>
          )}
        </RevealOnScroll>
      </div>
    </section>
  );
}

const COURSE_CATEGORIES = [
  'DSA',
  'Development',
  'Data Science',
  'Data Analytics',
  'Product Management',
  'Soft Skills',
];

const DIFFICULTY_COLORS = {
  'Beginner': '#fde68a', // Yellow
  'Intermediate': '#6ee7b7', // Green
  'Advanced': '#fca5a5', // Red
  'Crash Course': '#c4b5fd', // Purple
};

function fuzzyMatch(str, query) {
  if (!query) return true;
  str = (str || '').toLowerCase();
  query = query.toLowerCase();
  return str.includes(query);
}

function HeartIcon({ filled, ...props }) {
  return filled ? (
    <svg viewBox="0 0 24 24" fill="#ef4444" stroke="#ef4444" strokeWidth="2" width="24" height="24" aria-hidden="true" {...props}>
      <path d="M12 21C12 21 4 13.36 4 8.5C4 5.42 6.42 3 9.5 3C11.24 3 12.91 3.81 14 5.08C15.09 3.81 16.76 3 18.5 3C21.58 3 24 5.42 24 8.5C24 13.36 16 21 16 21H12Z" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" width="24" height="24" aria-hidden="true" {...props}>
      <path d="M12 21C12 21 4 13.36 4 8.5C4 5.42 6.42 3 9.5 3C11.24 3 12.91 3.81 14 5.08C15.09 3.81 16.76 3 18.5 3C21.58 3 24 5.42 24 8.5C24 13.36 16 21 16 21H12Z" />
    </svg>
  );
}

function SkeletonCard() {
  return (
    <article className="skeleton-card flex flex-col p-0 overflow-hidden rounded-2xl min-h-[180px] transition-colors duration-500" aria-hidden="true">
      <div className="skeleton-card-image rounded-t-2xl" />
      <div className="flex flex-col gap-2 p-4">
        <div className="skeleton-text skeleton-text-short skeleton-delay-1" />
        <div className="skeleton-text skeleton-text-medium skeleton-delay-2" />
        <div className="flex gap-2 mb-2">
          <div className="skeleton-text skeleton-delay-3" style={{ width: '3rem', height: '1.5rem' }} />
          <div className="skeleton-text skeleton-delay-4" style={{ width: '2.5rem', height: '1.5rem' }} />
          <div className="skeleton-text skeleton-delay-5" style={{ width: '2rem', height: '1.5rem' }} />
        </div>
        <div className="skeleton-text skeleton-delay-6" style={{ height: '2.5rem' }} />
      </div>
    </article>
  );
}

// New skeleton components
function SkeletonFilter() {
  return (
    <div className="skeleton-filter skeleton-delay-1" aria-hidden="true" />
  );
}

function SkeletonSearch() {
  return (
    <div className="skeleton-search skeleton-delay-1" aria-hidden="true" />
  );
}

function SkeletonHeader() {
  return (
    <div className="skeleton-header skeleton-delay-1" aria-hidden="true" />
  );
}

function SkeletonFilters() {
  return (
    <div className="flex flex-wrap gap-3 justify-center items-center mb-8">
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonFilter key={i} />
      ))}
    </div>
  );
}

function SkeletonHeroButtons() {
  return (
    <div className="flex flex-col sm:flex-row gap-6 w-full justify-center">
      <div className="skeleton-text skeleton-delay-1" style={{ width: '140px', height: '3rem' }} />
      <div className="skeleton-text skeleton-delay-2" style={{ width: '140px', height: '3rem' }} />
    </div>
  );
}

function useWishlist() {
  const [wishlist, setWishlist] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem('wishlist') || '[]');
    } catch {
      return [];
    }
  });
  React.useEffect(() => {
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
  }, [wishlist]);
  const toggle = (id) => {
    setWishlist((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const isSaved = (id) => wishlist.includes(id);
  return { wishlist, toggle, isSaved };
}

function CourseCard({ course, onToggleWishlist, saved }) {
  const isFree = course.type === 'free';
  return (
    <motion.article
      className="glass-card flex flex-col p-0 overflow-hidden rounded-3xl min-h-[320px] shadow-xl group transition-colors duration-500 border border-blue-100 dark:border-blue-900"
      tabIndex={0}
      aria-label={`Course: ${course.title}`}
      whileHover={{ scale: 1.035, y: -6 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{ minHeight: '340px', height: '100%' }}
    >
      {/* Image with badges */}
      <div className="relative w-full" style={{ height: '60%' }}>
      <img
        src={course.thumbnail}
        alt={`Thumbnail for ${course.title} by ${course.platform}`}
          className="w-full object-cover rounded-t-3xl bg-gray-100 dark:bg-blue-950 transition-colors duration-500"
          style={{ height: '180px', minHeight: '160px', maxHeight: '220px' }}
        />
        {/* FREE/PAID badge */}
        <span className={`absolute top-4 left-4 px-4 py-1 rounded-full text-xs font-bold tracking-wide shadow-md ${isFree ? 'bg-green-500 text-white' : 'bg-blue-600 text-white'} uppercase select-none`}>
          {isFree ? 'FREE' : 'PAID'}
        </span>
        {/* Difficulty badge (moved to top-right) */}
        <span className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold bg-white/80 dark:bg-blue-900/80 text-blue-700 dark:text-blue-200 shadow transition-colors select-none">
          {course.difficulty}
        </span>
      </div>
      {/* Card Content */}
      <div className="flex flex-col gap-3 p-5 pt-3 flex-1 justify-between" style={{ minHeight: '120px' }}>
        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-0.5 select-none transition-colors duration-500">{course.platform}</span>
        <span className="text-2xl font-extrabold text-gray-900 dark:text-white mb-1 leading-tight transition-colors duration-500">{course.title}</span>
        <div className="flex gap-2 flex-wrap mb-2">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 transition-colors select-none">{course.duration}</span>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:text-gray-700 dark:text-gray-300 flex items-center gap-1 transition-colors select-none">★ {course.rating}</span>
        </div>
        <button
          className="mt-2 w-full px-0 py-3 rounded-full text-lg font-bold bg-gradient-to-r from-blue-600 to-blue-400 text-white shadow-md hover:scale-105 hover:shadow-lg active:scale-98 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          aria-label={`Explore course: ${course.title}`}
          onClick={() => window.open(course.link, '_blank', 'noopener noreferrer')}
        >
          Explore Course
        </button>
      </div>
    </motion.article>
  );
}

function AboutUs() {
  return (
    <section id="about-section" tabIndex={-1} className="glass-ui dark:glass-dark w-full max-w-[800px] mx-auto mb-10 px-4 md:px-6 lg:px-8 py-8 rounded-xl text-base transition-colors duration-500" style={{ background: 'rgba(224,242,254,0.85)' }}>
      <h2 className="text-2xl font-semibold mb-3 transition-colors duration-500" style={{ color: '#1E3A8A' }}>
        About Us
      </h2>
      <p className="mb-2 transition-colors duration-500" style={{ color: '#111827' }}>
        Cursory is a curated course discovery side-project by Ashutosh Rai. Built to help learners find the right courses, without the noise.
      </p>
      <p className="transition-colors duration-500" style={{ color: '#111827' }}>
        Connect on LinkedIn: <a href="https://www.linkedin.com/in/ashusomehow" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }} className="hover:text-blue-900 transition-colors duration-500">https://www.linkedin.com/in/ashusomehow</a>
      </p>
    </section>
  );
}

function Footer() {
  const navigate = useNavigate();
  const handleAboutClick = (e) => {
    e.preventDefault();
    navigate('/about');
    setTimeout(() => {
      const aboutSection = document.getElementById('about-section');
      if (aboutSection) {
        aboutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        aboutSection.focus?.();
      }
    }, 100);
  };
  return (
    <footer className="glass-ui dark:glass-dark w-full max-w-[800px] mx-auto mt-16 mb-6 px-4 md:px-6 lg:px-8 py-8 rounded-xl transition-colors duration-500" role="contentinfo">
      <div className="flex flex-col md:flex-row justify-between gap-8 text-gray-700 dark:text-gray-300 text-sm transition-colors duration-500">
        {/* Contribute */}
        <div className="flex-1 min-w-[140px]">
          <h3 className="font-semibold mb-3 text-base text-gray-900 dark:text-white transition-colors duration-500">Contribute</h3>
          <ul className="space-y-2">
            <li><a href="/suggest" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">Suggest a Course</a></li>
            <li><a href="/report" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">Report an Issue</a></li>
            <li><a href="/submit-course" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">Submit Your Course</a></li>
          </ul>
        </div>
        {/* Community */}
        <div className="flex-1 min-w-[140px]">
          <h3 className="font-semibold mb-3 text-base text-gray-900 dark:text-white transition-colors duration-500">Community</h3>
          <ul className="space-y-2">
            <li><a href="https://discord.gg/example" target="_blank" rel="noopener noreferrer" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">Discord</a></li>
            <li><a href="/forum" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">Forum</a></li>
            <li><a href="/subscribe" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">Newsletter</a></li>
          </ul>
        </div>
        {/* Legal */}
        <div className="flex-1 min-w-[140px]">
          <h3 className="font-semibold mb-3 text-base text-gray-900 dark:text-white transition-colors duration-500">Legal</h3>
          <ul className="space-y-2">
            <li><a href="/about" onClick={handleAboutClick} className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">About</a></li>
            <li><a href="/privacy" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">Privacy Policy</a></li>
            <li><a href="/terms" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">Terms of Service</a></li>
            <li><a href="/accessibility" className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition-colors duration-500">Accessibility</a></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}

function AboutPage() {
  return (
    <main>
      <section className="glass-ui dark:glass-dark w-full max-w-[800px] mx-auto my-8 min-h-[300px] px-4 md:px-6 lg:px-8 py-4 md:py-8 transition-colors duration-500">
        <AboutUs />
      </section>
    </main>
  );
}

function CourseList({ type, sectionRef }) {
  const [selectedCategory, setSelectedCategory] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState('');
  const [page, setPage] = React.useState(1);
  const COURSES_PER_PAGE = 10;
  const { wishlist, toggle, isSaved } = useWishlist();

  const { courses, loading } = useCourses({ type, selectedCategory, search, sort });

  // Pagination logic
  const totalPages = Math.ceil((courses?.length || 0) / COURSES_PER_PAGE);
  const paginatedCourses = courses.slice((page - 1) * COURSES_PER_PAGE, page * COURSES_PER_PAGE);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  React.useEffect(() => {
    setPage(1); // Reset to first page when filters/search/sort change
  }, [selectedCategory, search, sort, type]);

  return (
    <main>
      <section
        ref={sectionRef}
        className="glass-ui dark:glass-dark w-full max-w-[900px] mx-auto my-8 min-h-[300px] px-4 md:px-6 lg:px-8 py-4 md:py-8 transition-colors duration-500"
        aria-label={type === 'free' ? 'Free Courses' : 'Paid Courses'}
        style={{ background: 'rgba(30, 58, 138, 0.10)', border: '1px solid rgba(30, 58, 138, 0.18)', backdropFilter: 'blur(20px)' }}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <RevealOnScroll animationType="section-header">
            <h2 className="section-heading text-2xl font-semibold text-gray-900 dark:text-white m-0 text-center md:text-left transition-colors duration-500">
              {type === 'free' ? 'Free Courses' : 'Paid Courses'}
            </h2>
          </RevealOnScroll>
          <div className="relative w-full max-w-xs ml-auto">
          <input
            type="text"
              placeholder="Search courses, platforms, or categories..."
            value={search}
            onChange={e => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full bg-white/80 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-800 text-base font-medium text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-blue-200 shadow focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all duration-300"
            aria-label="Search courses"
          />
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-blue-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        {/* Filter Bar & Sort */}
        <div className="flex flex-wrap gap-3 justify-center items-center mb-8">
          {COURSE_CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`badge-category${selectedCategory === cat ? ' selected' : ''}`}
              style={{ fontWeight: selectedCategory === cat ? 800 : 600 }}
              onClick={() => setSelectedCategory(cat)}
              aria-pressed={selectedCategory === cat}
              aria-label={`Filter by ${cat}`}
            >
              {cat}
            </button>
          ))}
          {/* Show Clear Filter only if a filter is applied */}
          {selectedCategory && (
          <button
              className="clear-filter-btn"
            onClick={() => setSelectedCategory('')}
            aria-label="Clear filter"
          >
            ✕ Clear Filter
          </button>
          )}
          {/* Sort Dropdown */}
          <select
            className="sort-dropdown"
            value={sort}
            onChange={e => setSort(e.target.value)}
            aria-label="Sort courses"
          >
            <option value="">Sort by</option>
            <option value="duration-desc">Duration Descending</option>
            <option value="duration-asc">Duration Ascending</option>
            <option value="rating-desc">Rating Descending</option>
            <option value="rating-asc">Rating Ascending</option>
          </select>
        </div>
        {/* Course Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <RevealOnScroll key={`skeleton-${i}`} animationType="course-card-reveal" stagger={`reveal-stagger-${(i % 8) + 1}`}> 
                  <SkeletonCard />
              </RevealOnScroll>
            ))
          ) : paginatedCourses.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-400 text-lg opacity-80 col-span-full text-center transition-colors duration-500">
              No courses found.
            </div>
          ) : (
            paginatedCourses.map((course, idx) => (
              <RevealOnScroll key={course.id} animationType="course-card-reveal" stagger={`reveal-stagger-${(idx % 8) + 1}`}> 
                  <motion.div initial={false} animate={{}}>
                    <CourseCard course={course} onToggleWishlist={toggle} saved={isSaved(course.id)} />
                  </motion.div>
              </RevealOnScroll>
            ))
          )}
        </div>
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <button onClick={() => handlePageChange(page - 1)} disabled={page === 1} className="btn-outline px-4 py-2" aria-label="Previous Page">&lt;</button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={`btn-outline px-4 py-2 ${page === i + 1 ? 'bg-blue-600 text-white' : ''}`}
                aria-current={page === i + 1 ? 'page' : undefined}
              >
                {i + 1}
              </button>
            ))}
            <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} className="btn-outline px-4 py-2" aria-label="Next Page">&gt;</button>
          </div>
        )}
      </section>
    </main>
  );
}

function useScrollOnRoute(ref, active) {
  useEffect(() => {
    if (active && ref.current) {
      setTimeout(() => {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [active, ref]);
}

function SuggestCoursePage({ sectionRef, active }) {
  useScrollOnRoute(sectionRef, active);
  return (
    <main ref={sectionRef} className="min-h-screen flex items-center justify-center bg-transparent dark:bg-transparent">
      <form
        action="https://formspree.io/f/mzzglljr"
        method="POST"
        className="max-w-md w-full mx-auto p-6 glass-modal space-y-4 transition-colors duration-500"
      >
        <h2 className="text-xl font-semibold text-blue-700 dark:text-blue-300 text-center transition-colors duration-500">Suggest a Course</h2>
        <label className="block text-gray-800 dark:text-gray-200 transition-colors duration-500">
          Course Name:
          <input
            type="text"
            name="course_title"
            placeholder="e.g. JavaScript Basics by XYZ"
            className="w-full mt-1 p-3 glass-input transition-colors duration-500"
            required
          />
        </label>
        <label className="block text-gray-800 dark:text-gray-200 transition-colors duration-500">
          Platform / Channel:
          <input
            type="text"
            name="platform"
            placeholder="e.g. CodeWithHarry, Udemy"
            className="w-full mt-1 p-3 glass-input transition-colors duration-500"
            required
          />
        </label>
        <button
          type="submit"
          className="w-full p-3 glass-button btn-ripple transition-colors duration-500"
        >
          Submit Suggestion
        </button>
      </form>
    </main>
  );
}

function ReportIssuePage({ sectionRef, active }) {
  useScrollOnRoute(sectionRef, active);
  return (
    <main ref={sectionRef} className="min-h-screen flex items-center justify-center bg-transparent dark:bg-transparent">
      <form
        action="https://formspree.io/f/xovwookl"
        method="POST"
        className="max-w-md w-full mx-auto p-6 glass-modal space-y-4 transition-colors duration-500"
      >
        <h2 className="text-xl font-semibold text-blue-700 dark:text-blue-300 text-center transition-colors duration-500">Report an Issue</h2>
        <label className="block text-gray-800 dark:text-gray-200 transition-colors duration-500">
          Your Email (optional):
          <input
            type="email"
            name="email"
            placeholder="you@example.com"
            className="w-full mt-1 p-3 glass-input transition-colors duration-500"
          />
        </label>
        <label className="block text-gray-800 dark:text-gray-200 transition-colors duration-500">
          What would you like to report?
          <select
            name="issue_type"
            className="w-full mt-1 p-3 glass-input transition-colors duration-500"
          >
            <option value="">Select an option</option>
            <option value="Dead Link">Dead Link</option>
            <option value="Incorrect Information">Incorrect Information</option>
            <option value="Spam / Low Quality">Spam / Low Quality</option>
            <option value="Other">Other</option>
          </select>
        </label>
        <label className="block text-gray-800 dark:text-gray-200 transition-colors duration-500">
          Message (e.g. link, course name, issue details):
          <textarea
            name="message"
            placeholder="This course link doesn't work... or This course is spam..."
            className="w-full mt-1 p-3 glass-input resize-none transition-colors duration-500"
            rows="4"
          ></textarea>
        </label>
        <button
          type="submit"
          className="w-full p-3 glass-button btn-ripple transition-colors duration-500"
        >
          Submit Report
        </button>
      </form>
    </main>
  );
}

function AppContent() {
  const courseSectionRef = useRef(null);
  const suggestSectionRef = useRef(null);
  const reportSectionRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleHeroNavigate = (path) => {
    navigate(path);
    // scroll will be handled by useEffect in the section
  };
  const handleNavClick = (path, ref) => {
    navigate(path);
    setTimeout(() => {
      ref?.current?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-500">
      <Navbar
        onNavClick={{
          free: () => handleNavClick('/free', courseSectionRef),
          paid: () => handleNavClick('/paid', courseSectionRef),
          suggest: () => handleNavClick('/suggest', suggestSectionRef),
          report: () => handleNavClick('/report', reportSectionRef),
        }}
      />
      <Hero onNavigate={handleHeroNavigate} />
      <ScrollToCourseSection sectionRef={courseSectionRef} />
      <Routes>
        <Route path="/" element={<div />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/search" element={<div />} />
        <Route path="/suggest" element={<SuggestCoursePage sectionRef={suggestSectionRef} active={location.pathname === '/suggest'} />} />
        <Route path="/report" element={<ReportIssuePage sectionRef={reportSectionRef} active={location.pathname === '/report'} />} />
        <Route path="/free" element={<CourseList type="free" sectionRef={courseSectionRef} />} />
        <Route path="/paid" element={<CourseList type="paid" sectionRef={courseSectionRef} />} />
        <Route path="/submit-course" element={<div />} />
        <Route path="/forum" element={<div />} />
        <Route path="/subscribe" element={<div />} />
        <Route path="/privacy" element={<div />} />
        <Route path="/terms" element={<div />} />
        <Route path="/accessibility" element={<div />} />
      </Routes>
      <Footer />
    </div>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
